import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UseType } from 'go-modules/models/use-type/use-type.interface';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { NgxAiPromptService } from '@go-modules/services/ai-prompt/ai-prompt.service';
import { SignUpService } from '@go-modules/services/sign-up/sign-up.service';
import {
	BehaviorSubject,
	Observable,
	combineLatest,
	forkJoin,
	of,
	Subject
} from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { LlmDefinition } from '@go-modules/interfaces/llm-definition';
import { openManagedMatDialog } from '@go-modules/utilities';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';

export interface DefaultAiMarkersDialogData {
	mode: string;
	useTypeId: number | null;
	account?: any;
	dialogTitleId?: string;
}

export interface DefaultAiMarkersDialogResultData {
	useTypeId: number;
	useTypeName: string;
	llmDefinitions: LlmDefinition[];
}

@Component({
	selector: 'default-ai-markers-dialog',
	templateUrl: './default-ai-markers-dialog.component.html',
	styleUrls: ['./default-ai-markers-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class DefaultAIMarkersDialogComponent implements OnInit, OnDestroy {
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public filterBy$ = new BehaviorSubject<Pick<UseType, 'use_type_id'> | null>({use_type_id: 1});
	public allLlmDefinitions$: Observable<LlmDefinition[]>;
	public useTypes: UseType[];
	public mode;
	public selectedUseType: UseType | null = null;
	public defaultLlmDefinitions: LlmDefinition[] = [];
	private destroyed$ = new Subject<void>();

	public static open (
		dialog: MatDialog,
		config?: MatDialogConfig<DefaultAiMarkersDialogData>
	): MatDialogRef<DefaultAIMarkersDialogComponent, DefaultAiMarkersDialogResultData | null> {
		const dialogTitleId = config?.data?.dialogTitleId ?? DefaultAIMarkersDialogComponent.getDialogTitleId();

		return openManagedMatDialog(dialog, DefaultAIMarkersDialogComponent, {
			...(config ?? {}),
			data: {
				...(config?.data ?? {}),
				dialogTitleId
			},
			ariaLabelledBy: dialogTitleId
		});
	}

	private static getDialogTitleId (): string {
		return `default-ai-markers-dialog-title-${UniqueIdGeneratorUtil.getId()}`;
	}

	constructor (
		public dialogRef: MatDialogRef<DefaultAIMarkersDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: DefaultAiMarkersDialogData,
		private signUpService: SignUpService,
		private aiPromptService: NgxAiPromptService,
		@Inject(selectedServiceToken) private selectedService: SelectedService
	) {
		this.data.dialogTitleId ??= DefaultAIMarkersDialogComponent.getDialogTitleId();
	}

	public ngOnInit (): void {
		this.mode = this.data.mode;
		this.loading$.next(true);
		const account = this.getContextAccount();

		forkJoin({
			useTypes: this.signUpService.getUseTypes(false, true),
			prompts: account == null ? of([]) : this.aiPromptService.getLlmDefinitions(account)
		}).pipe(takeUntil(this.destroyed$)).subscribe(({useTypes, prompts}) => {
			this.useTypes = this.filterAvailableUseTypesWithDefaults(useTypes, prompts);
			const defaultUseTypeId = this.data.useTypeId ? this.data.useTypeId : account?.use_type_id;
			this.selectedUseType = this.useTypes.find((useType) =>
				Number(useType.use_type_id) === Number(defaultUseTypeId)) ?? this.useTypes[0] ?? null;
			this.filterUseType(this.selectedUseType);
			this.allLlmDefinitions$ = combineLatest([
				of(prompts as LlmDefinition[]),
				this.filterBy$
			]).pipe(
				map(([prompts, filterBy]) => {
					this.defaultLlmDefinitions = prompts.filter(
						(prompt: LlmDefinition) => prompt.use_types
							.some((useType) => useType.use_type_id === filterBy?.use_type_id && useType.is_default)
					);
					return this.defaultLlmDefinitions;
				})
			);

			this.loading$.next(false);
		});
	}

	public ngOnDestroy (): void {
		this.destroyed$.next();
		this.destroyed$.complete();
	}

	public filterUseType (useType: UseType | null): void {
		this.selectedUseType = useType;
		this.filterBy$.next(useType);
	}

	public cancel (): void {
		this.dialogRef.close(null);
	}

	public canSave (): boolean {
		return this.selectedUseType != null;
	}

	public save (): void {
		if (!this.canSave() || this.selectedUseType == null) {
			return;
		}

		const selectedUseType = this.selectedUseType;
		const data: DefaultAiMarkersDialogResultData = {
			useTypeId: selectedUseType.use_type_id,
			useTypeName: selectedUseType.name,
			llmDefinitions: this.defaultLlmDefinitions
		};
		this.dialogRef.close(data);
	}

	private filterAvailableUseTypesWithDefaults (useTypes, prompts) {
		return useTypes.filter((useType: any) => {
			return prompts.some((prompt: any) =>
				prompt.use_types.some((useTypeInPrompt: any) =>
					useTypeInPrompt.use_type_id === useType.use_type_id && useTypeInPrompt.is_default
				)
			);
		});
	}

	private getContextAccount (): any {
		return this.hasExplicitDialogValue('account')
			? this.data.account ?? null
			: this.selectedService.getAccount();
	}

	private hasExplicitDialogValue (key: keyof DefaultAiMarkersDialogData): boolean {
		return Object.prototype.hasOwnProperty.call(this.data, key);
	}
}
